import type { ReactNode } from "react";
import VideoFeedPageSkeleton from "@/components/skeletons/VideoFeedPageSkeleton";
import { FollowingVideoFeedSkeleton } from "@/components/skeletons/FollowingFeedPageSkeleton";
import SchedulesListPageSkeleton from "@/components/skeletons/SchedulesListPageSkeleton";
import ScheduleDetailPageSkeleton from "@/components/skeletons/ScheduleDetailPageSkeleton";
import ComparisonListPageSkeleton from "@/components/skeletons/ComparisonListPageSkeleton";
import ComparisonDetailPageSkeleton from "@/components/skeletons/ComparisonDetailPageSkeleton";
import GenericMainContentSkeleton from "@/components/skeletons/GenericMainContentSkeleton";
import {
  SettingsAppliedJobsPageSkeleton,
  SettingsSavedPageSkeleton,
} from "@/components/skeletons/SettingsSavedAppliedSkeletons";

/** /me/* — settings-style: white card, form rows */
function MeAreaSkeleton() {
  return (
    <div
      className="skeleton-stage w-full max-w-4xl mx-auto p-6 lg:p-8"
      aria-busy
      aria-label="Loading"
    >
      <div className="h-9 w-56 skeleton-shimmer rounded-lg mb-8" />
      <div className="bg-white rounded-2xl border border-border/60 p-6 lg:p-8 space-y-6 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="h-28 w-28 rounded-full skeleton-shimmer shrink-0" />
          <div className="flex-1 space-y-3 w-full min-w-0">
            <div className="h-6 w-48 skeleton-shimmer rounded" />
            <div className="h-4 w-full max-w-md skeleton-shimmer rounded" />
            <div className="h-10 w-40 skeleton-shimmer rounded-lg" />
          </div>
        </div>
        <div className="h-px bg-border" />
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 w-32 skeleton-shimmer rounded" />
            <div className="h-12 w-full skeleton-shimmer rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * /profiles/[id] — يطابق `profiles/[profileId]/page.tsx`:
 * عمود أيسر ثابت `lg:w-[520px]`: بطاقة معلومات (أفاتار مركزي، أزرار، إحصائيات) + فاصل +
 * معرضَا فيديو أفقيان (w-40 × h-72) + تعليقات الدعم.
 * عمود أيمن `flex-1`: عناصر بيضاء `rounded-3xl p-6` (نبذة، أقسام متتابعة مع فواصل).
 */
function ProfileGalleryStripSkeleton() {
  return (
    <div className="w-full pl-8 flex flex-col justify-start items-start gap-3">
      <div className="h-7 w-36 max-w-[calc(100%-2rem)] skeleton-shimmer rounded-md" />
      <div className="w-full inline-flex justify-start items-center gap-3 overflow-hidden pr-8 pb-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="w-40 h-72 rounded-xl skeleton-shimmer shrink-0"
          />
        ))}
      </div>
    </div>
  );
}

function ProfilePublicSkeleton() {
  return (
    <div
      className="skeleton-stage w-full p-4 lg:p-8 flex flex-wrap justify-center items-start gap-8"
      aria-busy
      aria-label="Loading"
    >
      <div className="w-full lg:w-[520px] self-stretch py-8 bg-white rounded-3xl flex flex-col justify-start items-start gap-6 overflow-hidden border border-border/40 shadow-sm">
        <div className="self-stretch px-8 flex flex-col justify-start items-center gap-4 w-full">
          <div className="h-28 w-28 rounded-full skeleton-shimmer shrink-0" />
          <div className="flex flex-wrap justify-center gap-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-10 w-10 rounded-full skeleton-shimmer"
              />
            ))}
          </div>
          <div className="flex flex-col items-center gap-2 w-full">
            <div className="h-7 w-52 max-w-full skeleton-shimmer rounded-md" />
            <div className="h-5 w-44 max-w-full skeleton-shimmer rounded-md opacity-95" />
          </div>
          <div className="h-4 w-40 skeleton-shimmer rounded" />
          <div className="self-stretch inline-flex justify-center items-start gap-4 flex-wrap">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[4.25rem] w-[5.5rem] rounded-2xl skeleton-shimmer"
              />
            ))}
          </div>
        </div>

        <SeparatorLine />

        <ProfileGalleryStripSkeleton />
        <ProfileGalleryStripSkeleton />

        <div className="w-full pl-8 pr-8 flex flex-col gap-3">
          <div className="h-7 w-44 skeleton-shimmer rounded-md" />
          <div className="h-24 w-full rounded-xl skeleton-shimmer" />
        </div>
      </div>

      <div className="w-full lg:w-auto flex-1 min-w-0 flex flex-col gap-6 bg-white rounded-3xl p-6 border border-border/40 shadow-sm">
        <div className="space-y-3">
          <div className="h-6 w-28 skeleton-shimmer rounded" />
          <div className="h-3 w-full skeleton-shimmer rounded" />
          <div className="h-3 w-full skeleton-shimmer rounded" />
          <div className="h-3 w-full max-w-2xl skeleton-shimmer rounded opacity-90" />
        </div>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="contents">
            <hr className="border-stone-200" />
            <div className="space-y-3">
              <div className="h-5 w-40 skeleton-shimmer rounded" />
              <div className="h-3 w-full skeleton-shimmer rounded" />
              <div className="h-3 w-full max-w-xl skeleton-shimmer rounded" />
              <div className="h-3 w-full max-w-lg skeleton-shimmer rounded opacity-90" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SeparatorLine() {
  return <div className="h-px self-stretch bg-stone-300 w-[calc(100%-2rem)] mx-auto" />;
}

/** `/settings`, `/me/profile`, … — قائمة إعدادات + عمود محتوى */
export function MeProfileSettingsSkeleton() {
  return (
    <div
      className="skeleton-stage container mx-auto py-8 flex-grow"
      aria-busy
      aria-label="Loading"
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="md:col-span-1 bg-white p-6 rounded-lg shadow-sm border border-border/40 space-y-4">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div
              key={i}
              className={`h-4 skeleton-shimmer rounded ${i % 2 === 0 ? "w-[80%]" : "w-full"}`}
            />
          ))}
        </div>
        <div className="md:col-span-3 bg-white p-8 rounded-3xl flex flex-col gap-y-6 border border-border/40 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <div className="h-32 w-32 rounded-full skeleton-shimmer shrink-0" />
            <div className="flex-1 space-y-3 w-full min-w-0">
              <div className="h-6 w-52 skeleton-shimmer rounded" />
              <div className="h-4 w-full max-w-md skeleton-shimmer rounded" />
              <div className="h-10 w-36 skeleton-shimmer rounded-lg" />
            </div>
          </div>
          <div className="h-px bg-border" />
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 w-36 skeleton-shimmer rounded" />
              <div className="h-11 w-full skeleton-shimmer rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** /watch/[id] — video focus */
function WatchPageSkeleton() {
  return (
    <div
      className="skeleton-stage w-full max-w-lg mx-auto px-4 py-6 flex flex-col items-center"
      aria-busy
      aria-label="Loading"
    >
      <div className="w-full aspect-[9/16] max-h-[70vh] rounded-2xl skeleton-shimmer mb-6" />
      <div className="w-full space-y-3">
        <div className="h-6 w-[75%] max-w-sm skeleton-shimmer rounded mx-auto" />
        <div className="h-4 w-1/2 max-w-xs skeleton-shimmer rounded mx-auto" />
      </div>
    </div>
  );
}

/** terms, privacy, help, faq — single column doc */
function LegalStylePageSkeleton() {
  return (
    <div
      className="skeleton-stage w-full max-w-3xl mx-auto p-6 lg:p-10"
      aria-busy
      aria-label="Loading"
    >
      <div className="h-10 w-2/3 max-w-md skeleton-shimmer rounded-lg mb-8" />
      <div className="space-y-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className={
              i % 3 === 0
                ? "h-4 w-[85%] skeleton-shimmer rounded"
                : "h-4 w-full skeleton-shimmer rounded"
            }
          />
        ))}
      </div>
      <div className="h-40 w-full skeleton-shimmer rounded-xl mt-10" />
    </div>
  );
}

/**
 * Resolves a professional skeleton for any path (no locale prefix), e.g. `/me/profile`, `/profiles/x`.
 */
export function skeletonForRoute(pathWithoutLocale: string): ReactNode {
  const p = pathWithoutLocale || "/";
  if (p === "/" || p === "") return <VideoFeedPageSkeleton />;
  if (p === "/following") return <FollowingVideoFeedSkeleton />;
  if (p === "/schedules") return <SchedulesListPageSkeleton />;
  if (p.startsWith("/schedules/")) return <ScheduleDetailPageSkeleton />;
  if (p === "/comparison") return <ComparisonListPageSkeleton />;
  if (p.startsWith("/comparison/")) return <ComparisonDetailPageSkeleton />;
  if (p.startsWith("/watch/")) return <WatchPageSkeleton />;
  if (p.startsWith("/profiles/")) return <ProfilePublicSkeleton />;
  if (/\/jobs\/[^/]+\/watch/.test(p)) return <WatchPageSkeleton />;
  /** `/me` — عرض Personal/Company بدون قائمة إعدادات جانبية */
  if (p === "/me") return <MeAreaSkeleton />;
  if (p === "/settings/saved") return <SettingsSavedPageSkeleton />;
  if (p === "/settings/applied-jobs") return <SettingsAppliedJobsPageSkeleton />;
  /** `/settings`, `/settings/profile`, … */
  if (p === "/settings" || p.startsWith("/settings/")) {
    return <MeProfileSettingsSkeleton />;
  }
  /**
   * `/me/profile`, `/me/saved`, `/me/jobs`, … — نفس تخطيط الشبكة في الصفحات
   * (انظر `SettingsMenuWrapper` + عمود المحتوى).
   */
  if (p.startsWith("/me/")) return <MeProfileSettingsSkeleton />;
  if (p === "/apply-job" || p.startsWith("/apply-job/")) {
    return <GenericMainContentSkeleton />;
  }
  if (
    ["/terms", "/privacy", "/help", "/faq", "/data-deletion"].some(
      (x) => p === x || p.startsWith(`${x}/`),
    )
  ) {
    return <LegalStylePageSkeleton />;
  }
  return <GenericMainContentSkeleton />;
}
